import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  Divider,
  Stack,
} from '@mui/material';
import {
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
  YouTube as YouTubeIcon,
  MailOutline,
  PhoneOutlined,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useStorefrontConfig } from '../StorefrontConfigProvider';

const StorefrontFooter = () => {
  const { settings } = useStorefrontConfig();
  const currentYear = new Date().getFullYear();
  const storeName = settings?.store_name || 'Store';

  const social = [
    { icon: <FacebookIcon />, url: settings?.facebook_url, label: 'Facebook' },
    { icon: <InstagramIcon />, url: settings?.instagram_url, label: 'Instagram' },
    { icon: <TwitterIcon />, url: settings?.twitter_url, label: 'Twitter' },
    { icon: <YouTubeIcon />, url: settings?.youtube_url, label: 'YouTube' },
  ].filter((s) => s.url);

  const shopLinks = [
    { label: 'All Products', path: '/shop/products' },
    { label: 'New Arrivals', path: '/shop/products?section=new' },
    { label: 'Best Sellers', path: '/shop/products?section=bestsellers' },
    { label: 'Sale', path: '/shop/products?section=sale' },
  ];
  const accountLinks = [
    { label: 'My Account', path: '/shop/my-account' },
    { label: 'My Orders', path: '/shop/my-orders' },
    { label: 'My Addresses', path: '/shop/my-addresses' },
    { label: 'Wishlist', path: '/shop/wishlist' },
  ];
  // Policy links rendered only when content is configured.
  const policyLinks = [
    settings?.shipping_policy && { label: 'Shipping Policy', path: '/shop/policies/shipping' },
    settings?.return_policy && { label: 'Returns & Refunds', path: '/shop/policies/returns' },
    settings?.privacy_policy && { label: 'Privacy Policy', path: '/shop/policies/privacy' },
    settings?.terms_and_conditions && { label: 'Terms & Conditions', path: '/shop/policies/terms' },
  ].filter(Boolean);

  const Column = ({ title, links }) =>
    links.length > 0 && (
      <Grid item xs={6} sm={3} md={2}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
          {links.map((link) => (
            <li key={link.path + link.label} style={{ marginBottom: 8 }}>
              <Link
                component={RouterLink}
                to={link.path}
                underline="hover"
                sx={{ color: 'grey.400', fontSize: '0.875rem', '&:hover': { color: 'white' } }}
              >
                {link.label}
              </Link>
            </li>
          ))}
        </Box>
      </Grid>
    );

  return (
    <Box component="footer" sx={{ bgcolor: 'grey.900', color: 'white', py: 6, mt: 8 }}>
      <Container maxWidth="xl">
        <Grid container spacing={4}>
          <Grid item xs={12} md={3}>
            {settings?.logo ? (
              <Box component="img" src={settings.logo} alt={storeName} sx={{ height: 36, mb: 2, filter: 'brightness(0) invert(1)' }} />
            ) : (
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 800 }}>{storeName}</Typography>
            )}
            <Typography variant="body2" sx={{ mb: 2, color: 'grey.400' }}>
              {settings?.store_description || settings?.store_tagline || 'Quality products, delivered to your door.'}
            </Typography>
            <Stack spacing={0.5} sx={{ mb: 2 }}>
              {settings?.contact_email && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'grey.400' }}>
                  <MailOutline sx={{ fontSize: 16 }} />
                  <Link href={`mailto:${settings.contact_email}`} underline="hover" sx={{ color: 'grey.400', fontSize: '0.85rem' }}>
                    {settings.contact_email}
                  </Link>
                </Box>
              )}
              {settings?.contact_phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'grey.400' }}>
                  <PhoneOutlined sx={{ fontSize: 16 }} />
                  <Typography variant="body2">{settings.contact_phone}</Typography>
                </Box>
              )}
            </Stack>
            {social.length > 0 && (
              <Box>
                {social.map((s) => (
                  <IconButton
                    key={s.label}
                    component="a"
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    sx={{ color: 'white', '&:hover': { color: 'primary.light' } }}
                  >
                    {s.icon}
                  </IconButton>
                ))}
              </Box>
            )}
          </Grid>

          <Column title="Shop" links={shopLinks} />
          <Column title="My Account" links={accountLinks} />
          <Column title="Customer Service" links={policyLinks} />
        </Grid>

        <Divider sx={{ my: 3, bgcolor: 'grey.700' }} />

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'grey.400' }}>
            © {currentYear} {storeName}. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default StorefrontFooter;
